import { ApiError, apiFetch } from "./api-client";
import type { DailyTask, SwipeResult, UserSettings, Word } from "@/types";

// ─── date helpers (kept client-side because UI components depend on them) ──
function localDateStr(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function getEffectiveDate(dayStartHour: number): string {
  const now = new Date();
  const d = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + (now.getHours() < dayStartHour ? -1 : 0),
  );
  return localDateStr(d);
}

export function getWeeklyStudiedDates(
  lastStudyDate: string | null,
  streak: number,
  weekDates: string[],
): string[] {
  if (!lastStudyDate) return [];
  const weekSet = new Set(weekDates);
  if (!weekSet.has(lastStudyDate)) return [];
  if (streak <= 0) return [lastStudyDate];

  const result: string[] = [];
  const [y, m, d] = lastStudyDate.split("-").map(Number);
  const cursor = new Date(y, m - 1, d, 12, 0, 0);
  for (let i = 0; i < streak; i++) {
    const ds = localDateStr(cursor);
    if (weekSet.has(ds)) result.push(ds);
    cursor.setDate(cursor.getDate() - 1);
  }
  return result;
}

// ─── Server-backed API ─────────────────────────────────────────────────────
type DailyTaskResponse = Array<{
  id: string;
  task_type: DailyTask["task_type"];
  word_ids: string[];
  is_completed: boolean;
  words: Word[];
}>;

export async function initUser(): Promise<void> {
  await apiFetch("/api/init", { method: "POST" });
}

export async function getDailyTasks(): Promise<DailyTask[]> {
  return apiFetch<DailyTaskResponse>("/api/daily-tasks");
}

export async function markTaskCompleted(taskId: string): Promise<void> {
  await apiFetch(`/api/tasks/${encodeURIComponent(taskId)}/complete`, { method: "POST" });
}

export async function saveSwipeResult(wordId: string, result: SwipeResult): Promise<void> {
  await apiFetch("/api/swipe", {
    method: "POST",
    body: JSON.stringify({ word_id: wordId, result }),
  });
}

export async function saveSessionResults(
  results: Record<string, SwipeResult>,
  durationSeconds: number,
  options?: { skipStreak?: boolean },
): Promise<void> {
  await apiFetch("/api/session", {
    method: "POST",
    body: JSON.stringify({
      results,
      duration_seconds: durationSeconds,
      skip_streak: !!options?.skipStreak,
    }),
  });
}

export async function getWordsByWeek(week: number): Promise<Word[]> {
  return apiFetch<Word[]>(`/api/words?week=${week}`);
}

export async function getUserSettings(): Promise<UserSettings | null> {
  return apiFetch<UserSettings>("/api/user-settings").catch(() => null);
}

export async function updateUserSettings(
  updates: Partial<Omit<UserSettings, "user_id">>,
): Promise<{ error: string | null }> {
  try {
    await apiFetch("/api/user-settings", {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "unknown error" };
  }
}

export async function getStudiedDates(days = 7): Promise<string[]> {
  return apiFetch<string[]>(`/api/studied-dates?days=${days}`);
}

export async function getCurrentWeekStatus(): Promise<{
  week: number;
  dayInWeek: number;
} | null> {
  return apiFetch<{ week: number; dayInWeek: number } | null>("/api/week-status").catch(() => null);
}

export async function autoStreakOnRestDay(): Promise<boolean> {
  const res = await apiFetch<{ applied: boolean }>("/api/rest-day-streak", { method: "POST" });
  return !!res.applied;
}

export interface WeekStage {
  week: number;
  status: "completed" | "current" | "locked";
  completedTasks: number;
  totalTasks: number;
}

export async function getJourneyProgress(): Promise<{
  currentWeek: number;
  startDate: string;
  weeks: WeekStage[];
} | null> {
  return apiFetch<{
    currentWeek: number;
    startDate: string;
    weeks: WeekStage[];
  } | null>("/api/journey").catch(() => null);
}

export async function deleteAccount(): Promise<void> {
  await apiFetch("/api/account", { method: "DELETE" });
}

// ─── auth ─────────────────────────────────────────────────────────────────
export interface CurrentUser {
  id: string;
  email: string | null;
}

/** ログイン中なら本人情報、未ログインなら null。 */
export async function getCurrentUser(): Promise<CurrentUser | null> {
  try {
    return await apiFetch<CurrentUser>("/api/auth/me");
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    throw e;
  }
}

export async function requestLoginLink(email: string): Promise<void> {
  await apiFetch("/api/auth/request-link", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function logout(): Promise<void> {
  await apiFetch("/api/auth/logout", { method: "POST" });
}
